import { Icon } from '@design-system/components/atoms'
import { typo } from '@design-system/styles'
import { css } from '@emotion/react'
import { s3URL } from '@mathflat/common/util'
import clsx from 'clsx'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { isMobile, isTablet } from 'react-device-detect'
import { matchPath, useHistory } from 'react-router'
import { NavLink } from 'react-router-dom'

import type { NavInfoList } from '@/component/organisms/Navigation/SideNavigation/SideNavigation'
import commonRepo from '@/repository/common.repo'
import { toastFailure } from '@/utils/toast'

import { Badge } from '../Badge'

// e.detail === 0 은 키보드 클릭(Enter)을 의미하므로 NavLink가 정상적으로 네비게이션하도록 허용
const preventMouseClickNavigation = (e: React.MouseEvent<HTMLAnchorElement>) => {
  if (e.detail !== 0) e.preventDefault()
}

type Props = {
  urlPatterns?: string[]
  onClick?: () => void
  variant?: 'top' | 'section'
} & React.ComponentProps<NavLink> &
  Pick<
    Exclude<NavInfoList[number]['items'][number], false | undefined>,
    'isNew' | 'hasShareIcon' | 'badgeProps' | 'badgePosition' | 'matchUrl' | 'restrictionMessage'
  >

function SideNavigationLink({
  to,
  children: label,
  matchUrl,
  isNew,
  hasShareIcon,
  onClick,
  variant = 'top',
  restrictionMessage,
  badgePosition = 'left',
  badgeProps,
  ...props
}: Props) {
  const history = useHistory()
  const [isPressed, setPressed] = useState<boolean>(false)
  const lastTo = typeof to === 'function' ? to(commonRepo.router.location) : to
  const isTouchableDevice = isMobile || isTablet
  const isRestricted = !!restrictionMessage
  const { children: badgeChildren, ..._badgeProps } = badgeProps || {}

  const handleClickStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    if (isRestricted) {
      toastFailure(restrictionMessage!)
      return
    }
    onClick?.()
    setPressed(true)
  }

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isRestricted) {
      e.preventDefault()
      e.stopPropagation()
      return
    }
    preventMouseClickNavigation(e)
  }

  useEffect(() => {
    if (isPressed) {
      history.push(lastTo)
      setPressed(false)
    }
  }, [isPressed])

  return (
    <NavLink
      data-testid={`navigation-${label}`}
      data-component={`${label}탭`}
      to={to}
      css={variant === 'section' ? S.sectionStyle : S.topStyle}
      activeClassName="active"
      isActive={(match, location) => {
        const customMatch = matchPath(location.pathname, { path: matchUrl })
        return isPressed || (matchUrl ? !!customMatch : !!match)
      }}
      onClick={handleClick}
      onTouchStart={isTouchableDevice ? handleClickStart : undefined}
      onMouseDown={!isTouchableDevice ? handleClickStart : undefined}
      {...props}
    >
      {badgeProps && (
        <div css={S.badgeWrapper} className={clsx('badge-wrapper', badgePosition)}>
          <Badge {..._badgeProps}>{badgeChildren}</Badge>
        </div>
      )}
      {label}
      {hasShareIcon && <Icon name="share" size={14} color="var(--Colors-Gray-600)" />}
      {isNew && (
        <div css={S.newLabel}>
          <img
            src={s3URL.mathflatAssetsBucket.graphic('graphic-new.svg')}
            alt="new"
            width={14}
            height={14}
          />
        </div>
      )}
    </NavLink>
  )
}

function NestedSideNavigationLink({
  to,
  children: label,
  urlPatterns,
  isNew,
  onClick,
  ...props
}: Exclude<Props, 'badgePosition' | 'badgeProps'>) {
  const history = useHistory()
  const [isPressed, setPressed] = useState<boolean>(false)
  const lastTo = typeof to === 'function' ? to(commonRepo.router.location) : to
  const isTouchableDevice = isMobile || isTablet

  useEffect(() => {
    if (isPressed) {
      history.push(lastTo)
      setPressed(false)
    }
  }, [isPressed])

  const handleClickStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    onClick?.()
    setPressed(true)
  }

  return (
    <NavLink
      data-testid={`navigation-${label}`}
      data-component={`${label}탭`}
      to={to}
      css={S.nestedDefaultStyle}
      activeStyle={activeNestedStyle}
      isActive={(match, location) => {
        const customMatch = matchPath(location.pathname, { path: urlPatterns })
        return isPressed || (urlPatterns ? !!customMatch : !!match)
      }}
      onClick={preventMouseClickNavigation}
      onTouchStart={isTouchableDevice ? handleClickStart : undefined}
      onMouseDown={!isTouchableDevice ? handleClickStart : undefined}
      {...props}
    >
      {label}
      {isNew && (
        <div css={S.newLabel}>
          <img src={s3URL.mathflatAssetsBucket.graphic('graphic-new.svg')} alt="new" />
        </div>
      )}
    </NavLink>
  )
}

const baseLinkStyle = css`
  display: flex;
  align-items: center;
  gap: 4px;
  position: relative;
  width: 100%;
  padding: 0 12px;
  border-radius: var(--Radius-200);
  ${typo.body02};
  color: var(--Colors-Gray-900);
  transition: all 0.15s ease-in-out;

  &:hover {
    background: var(--Colors-Gray-200);
  }

  &:active {
    background: var(--Colors-Gray-300);
  }

  &.active {
    background: var(--Colors-Sky-100);
    color: var(--Colors-Sky-400);
    font-weight: 700;

    &:hover {
      background: var(--Colors-Sky-200);
    }
  }
`

const S = {
  topStyle: css`
    ${baseLinkStyle};
    height: 56px;
  `,
  sectionStyle: css`
    ${baseLinkStyle};
    height: 42px;
  `,

  newLabel: css`
    display: flex;
    align-items: center;
  `,

  nestedDefaultStyle: css`
    display: flex;
    align-items: center;
    position: relative;
    padding: 12px;
    height: 45px;
    ${typo.body02}
    background: var(--White);
    border: 1px solid transparent;
    border-radius: var(--Radius-100);
    color: var(--Colors-Gray-900);
    font-weight: 400;
    transition:
      background 0.15s ease-in-out,
      color 0.15s ease-in-out,
      border-color 0.15s ease-in-out;
  `,

  badgeWrapper: css`
    display: flex;

    &.left {
      justify-content: flex-start;
    }

    &.center {
      justify-content: center;
    }

    &.right {
      justify-content: flex-end;
    }
  `,
}

const activeNestedStyle = {
  borderRadius: 'var(--Radius-100)',
  background: 'var(--Colors-Sky-100)',
  fontWeight: '700',
  color: 'var(--Colors-Sky-400)',
  border: '1px solid var(--Colors-Sky-200)',
}

export { NestedSideNavigationLink, SideNavigationLink }
